import { useCallback } from "react"
import { pipe } from "fp-ts/function"
import * as TE from "fp-ts/TaskEither"
import * as E from "fp-ts/Either"
import { useBottomSheetStore } from "@/components/common/bottomSheet/store/useBottomSheetStore"
import { usePopupStore } from "@/components/common/popup/store/usePopupStore"
import { useToastStore } from "@/components/common/toast/store/useToastStore"
import { useBookshelfPhotoAnalysis } from "@/hooks/useBookshelfPhotoAnalysis"
import ToastMessage from "@/components/common/toast/ToastMessage"
import fetchClientBookShelfBulkCreate from "@/app/(tabs)/bookshelf/_fetch/fetchClientBookShelfBulkCreate"
import { useBookshelfExtractStore } from "../_store/useBookshelfExtractStore"
import { getValidSelectedIsbns } from "../_utils/entityConverter"
import { ShelfItemBookEntity } from "@/app/entities/ShelfEntity"
import { getBookRegistrationErrorMessage } from "@/app/(tabs)/bookshelf/_config/bookErrorMessages"
import usePromotionEvent from "@/app/components/PromotionEvent/hooks/usePromotionEvent"
import { logDfineryEvent } from "@/utils/dfinery"
interface UseBookshelfActionsProps {
  analysisId?: string
  books: ShelfItemBookEntity[]
  onComplete: () => void
}

// 헬퍼 함수들
const showErrorMessage = (
  error: unknown,
  showToast: (position: "bottom", content: React.ReactNode) => void,
) => {
  const errorMessage = getBookRegistrationErrorMessage(
    error as { status: number; code?: number; message?: string },
  )
  if (errorMessage !== "") {
    showToast("bottom", <ToastMessage message={errorMessage} />)
  }
}

const handleSuccess = async (
  selectedCount: number,
  showToast: (position: "bottom", content: React.ReactNode) => void,
  closeSheet: () => void,
  onComplete: () => void,
) => {
  console.log("Bulk 등록 성공")

  closeSheet()

  showToast("bottom", <ToastMessage message="도서를 내 서재에 담았어요." />)
  onComplete()
}

/**
 * 책장 분석 액션 관리 Hook
 * 도서 등록, 팝업 관리, 네비게이션 등의 사용자 액션을 담당
 */
export const useBookshelfActions = ({
  analysisId,
  books,
  onComplete,
}: UseBookshelfActionsProps) => {
  const { closeSheet } = useBottomSheetStore()
  const { openPopup, closePopup } = usePopupStore()
  const { showToast } = useToastStore()
  const { handleBookshelfPhotoAnalysis } = useBookshelfPhotoAnalysis()
  const { checkEvent } = usePromotionEvent()

  const { selectedBooks, toggleBookSelection, getSelectedCount } =
    useBookshelfExtractStore()

  // 닫기 확인 팝업
  const handleClose = useCallback(() => {
    openPopup(
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
        <div className="absolute left-1/2 top-1/2 flex h-[183px] w-[285px] -translate-x-1/2 -translate-y-1/2 flex-col items-start gap-6 rounded-lg bg-white px-4 pb-4 pt-8">
          <p className="flex h-[63px] w-[253px] items-center justify-center text-center text-[15px] font-normal leading-[142.9%] text-[#555555]">
            촬영한 책장의 도서 목록 화면을 닫으면,
            <br />
            다시 책장을 촬영해야 합니다.
            <br />
            계속하시겠어요?
          </p>

          <div className="flex h-12 w-[253px] flex-row items-center p-0">
            <button
              onClick={() => {
                closePopup()
                closeSheet()
              }}
              className="flex h-12 w-[126px] flex-col items-center justify-center gap-[10px] rounded-bl-lg rounded-tl-lg bg-[#E0E2E5] px-7 py-4"
            >
              <div className="flex h-[21px] w-[56px] flex-row items-center justify-center p-0">
                <span className="h-[21px] w-[56px] text-[15px] font-medium leading-[142.9%] text-[#242424]">
                  목록 닫기
                </span>
              </div>
            </button>
            <button
              onClick={() => {
                closePopup()
              }}
              className="flex h-12 w-[127px] flex-col items-center justify-center gap-[10px] rounded-br-lg rounded-tr-lg bg-[#0077FF] px-7 py-4"
            >
              <div className="flex h-[21px] w-[26px] flex-row items-center justify-center p-0">
                <span className="h-[21px] w-[26px] text-[15px] font-medium leading-[142.9%] text-white">
                  취소
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>,
    )
  }, [openPopup, closePopup, closeSheet])

  // 책장 다시 촬영
  const handleRetakePhoto = useCallback(() => {
    closeSheet()
    handleBookshelfPhotoAnalysis()
  }, [closeSheet, handleBookshelfPhotoAnalysis])

  // 내 서재에 도서 등록
  const handleAddToLibrary = useCallback(() => {
    // 조기 반환 - 유효성 검증
    if (!analysisId) {
      showToast(
        "bottom",
        <ToastMessage message="분석 정보를 찾을 수 없습니다." />,
      )
      return
    }

    const selectedIsbnList = getValidSelectedIsbns(selectedBooks, books)
    if (selectedIsbnList.length === 0) {
      showToast("bottom", <ToastMessage message="선택된 도서가 없습니다." />)
      return
    }

    // API 호출
    pipe(
      TE.tryCatch(
        () =>
          fetchClientBookShelfBulkCreate({
            isbn: selectedIsbnList,
            shelfAnalyzeId: analysisId,
          }),
        (error) => error,
      ),
      TE.fold(
        (error) => async () => {
          console.error("Bulk 등록 실패:", error)
          showErrorMessage(error, showToast)
        },
        (eitherResponse) => async () => {
          await pipe(
            eitherResponse,
            E.fold(
              async (error) => {
                console.error("API 에러:", error)
                showErrorMessage(error, showToast)
                closeSheet()
              },
              async () => {
                // 프로모션 이벤트 서재 책 등록 확인
                checkEvent("book")

                // Dfinery 로깅: 각 도서마다 camera 등록 방식으로 기록
                selectedIsbnList.forEach((isbn) => {
                  const book = books.find((b) => b.isbn === isbn)
                  if (book) {
                    logDfineryEvent("lib_add_book", {
                      book_name: book.title,
                      isbn: book.isbn,
                      register_method: "camera",
                    })
                  }
                })

                await handleSuccess(
                  selectedIsbnList.length,
                  showToast,
                  closeSheet,
                  onComplete,
                )
              },
            ),
          )
        },
      ),
    )()
  }, [
    analysisId,
    selectedBooks,
    books,
    showToast,
    closeSheet,
    onComplete,
    checkEvent,
  ])

  return {
    handleClose,
    handleRetakePhoto,
    handleAddToLibrary,
    toggleBookSelection,
    selectedCount: getSelectedCount(),
    hasSelectedBooks: getSelectedCount() > 0,
  }
}
